import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import TopBar from '../layout/TopBar';
import StatusBadge from '../components/StatusBadge';
import SchemaQuestionCard from '../components/SchemaQuestionCard';
import { useHGS } from '../context/HGSContext';
import { MODULE_SCHEMAS } from '../data/masterData';
import { computeResidualScore, bandFor, FRAUD_TRIGGER_IDS } from '../data/riskUniverse';
import { ArchitectureProgress } from './RiskUniversePage';
import { filterRowsForUser, canFillModuleForms } from '../data/rbac';
import RiskUnpublishedGate from '../components/RiskUnpublishedGate';
import MasterEmptyState from '../components/MasterEmptyState';
import FilterBanner from '../components/FilterBanner';
import { Plus, ShieldCheck, AlertTriangle, CheckCircle2, Code2, Sparkles, Lock, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

const ANSWERABLE = ['responseType', 'evidenceFile', 'currentSituation', 'countermeasure', 'targetCompletionDate'];

export default function FraudPage() {
  const { masters, masterMeta, formResponses, riskUniverse, user, riskPublished } = useHGS();
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get('filter');
  const schema = MODULE_SCHEMAS.fraud;
  const meta = masterMeta.fraud;
  const allRows = useMemo(() => filterRowsForUser(user, (masters.fraud || []).filter((r) => r.active !== false)), [masters, user]);
  const canEdit = canFillModuleForms(user);
  const masterUploaded = (masters.fraud || []).length > 0;

  // Compute triggering financial risks (R23–R27, R30 at Medium/High)
  const triggeringRisks = useMemo(() => {
    return (riskUniverse || [])
      .filter((r) => FRAUD_TRIGGER_IDS.includes(r.id))
      .map((r) => {
        const { inherent, residual } = computeResidualScore(r);
        return { ...r, inherent, residual, band: bandFor(residual) };
      })
      .filter((r) => r.band === 'Medium' || r.band === 'High');
  }, [riskUniverse]);

  const isTriggered = triggeringRisks.length > 0;

  // Apply filter from URL (?filter=pending|no-or-na|missing-evidence|overdue)
  const rows = useMemo(() => {
    if (!filter) return allRows;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return allRows.filter((r) => {
      const resp = formResponses[`fraud:${r.controlId}`];
      const v = resp?.values || {};
      switch (filter) {
        case 'missing-evidence':
          return resp && v.responseType && !v.evidenceFile;
        case 'pending':
          return !resp || !v.responseType;
        case 'no-or-na':
          return v.responseType === 'No' || v.responseType === 'N/A';
        case 'overdue': {
          if (!(v.responseType === 'No' || v.responseType === 'N/A')) return false;
          if (!v.targetCompletionDate) return false;
          const due = new Date(v.targetCompletionDate);
          return !Number.isNaN(due.getTime()) && due < today;
        }
        default:
          return true;
      }
    });
  }, [allRows, filter, formResponses]);

  const groups = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const key = r.fraudCategory || 'General';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    return Array.from(map.entries());
  }, [rows]);

  // Live dashboard metrics computed from current Fraud responses
  const { kpis, completion } = useMemo(() => {
    let answered = 0;
    let fraudGaps = 0;
    let missingEvidence = 0;
    let overdue = 0;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    allRows.forEach((r) => {
      const resp = formResponses?.[`fraud:${r.controlId}`];
      const v = resp?.values || {};
      if (resp && v.responseType) answered += 1;
      if (v.responseType === 'No' || v.responseType === 'N/A') fraudGaps += 1;
      if (resp && v.responseType && !v.evidenceFile) missingEvidence += 1;
      if ((v.responseType === 'No' || v.responseType === 'N/A') && v.targetCompletionDate) {
        const due = new Date(v.targetCompletionDate);
        if (!Number.isNaN(due.getTime()) && due < today) overdue += 1;
      }
    });
    const comp = allRows.length === 0 ? 0 : Math.round((answered / allRows.length) * 100);
    return { kpis: { fraudGaps, missingEvidence, overdue, answered }, completion: comp };
  }, [allRows, formResponses]);

  // Navigation helper for KPI tile / filter pill clicks
  const navigateFilter = (key) => {
    const next = new URLSearchParams(searchParams);
    if (!key || filter === key) next.delete('filter');
    else next.set('filter', key);
    setSearchParams(next, { replace: true });
  };

  return (
    <div>
      <TopBar
        title="Fraud Prevention Checklist"
        subtitle="Step 2 of 5 · Triggered by financial risks R23–R27, R30 · control checklist rendered live from master"
        breadcrumbs={['HGS Architecture', 'Fraud Prevention']}
        actions={
          <button className="inline-flex items-center gap-2 px-4 py-2 bg-[#CC0000] hover:bg-[#B00000] text-white text-sm font-semibold rounded-lg shadow-sm transition">
            <Plus className="w-4 h-4" />
            Add Control
          </button>
        }
      />
      <div className="p-8 space-y-5">
        <ArchitectureProgress step={2} />

        {/* If Operating Head hasn't published Risk Universe, block downstream work */}
        {!riskPublished?.published && <RiskUnpublishedGate user={user} />}

        {riskPublished?.published && !canEdit && (
          <div className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200" data-testid="review-mode-banner">
            <Lock className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
            <div className="text-[11px] text-slate-600 leading-relaxed">
              <span className="font-bold text-slate-900">Review-only mode</span> — Department Heads fill Fraud controls. As a {user?.role?.replace(/_/g, ' ')}, you can see their responses but cannot edit them. Approve / reject from the <span className="font-semibold">Approvals</span> page.
            </div>
          </div>
        )}

        {riskPublished?.published && !masterUploaded && (
          <MasterEmptyState moduleLabel="Fraud Prevention" moduleKey="fraud" accent="#0EA5E9" isAdmin={user?.role === 'admin'} />
        )}

        {riskPublished?.published && masterUploaded && (<>
        {/* Trigger status */}
        {!isTriggered ? (
          <div className="bg-white rounded-xl border border-amber-200 p-6 flex items-start gap-4" data-testid="fraud-gate-not-triggered">
            <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
              <Lock className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-bold text-slate-900">Not yet triggered</h3>
              <p className="text-sm text-slate-600 mt-1">
                The Fraud Prevention Checklist activates automatically when any financial risk (R23–R27, R30) is rated <b>Medium</b> or <b>High</b> in the Risk Universe.
                Currently all financial risks are at Watch/Low level.
              </p>
              <Link to="/risk" className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-[#CC0000] hover:underline">
                Go to Risk Universe → <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-sky-200 p-5" data-testid="fraud-gate-triggered">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-sky-100 text-sky-700 flex items-center justify-center">
                <AlertTriangle className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900">Fraud Checklist is active</h3>
                <p className="text-[11px] text-slate-500">Triggered by {triggeringRisks.length} financial risk{triggeringRisks.length > 1 ? 's' : ''} currently rated Medium/High:</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {triggeringRisks.map((r) => (
                <span key={r.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-sky-50 border border-sky-200 text-[11px] font-bold text-sky-900">
                  <span className="font-mono">{r.id}</span>
                  <span className="font-normal">·</span>
                  <span className="font-normal truncate max-w-[240px]">{r.description}</span>
                  <span className={cn('px-1.5 py-0.5 rounded text-[9px] uppercase font-bold', r.band === 'High' ? 'bg-red-600 text-white' : 'bg-amber-400 text-amber-950')}>{r.band}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {isTriggered && (
          <>
        <div className="flex items-start gap-3 p-4 rounded-xl bg-gradient-to-r from-slate-900 to-[#0c2540] text-white">
          <Sparkles className="w-4 h-4 text-sky-300 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold flex items-center gap-2 flex-wrap">
              Forms below are rendered live from
              <span className="font-mono px-2 py-0.5 rounded bg-white/10 text-sky-300">{schema.label}</span>
              <span className="font-mono px-2 py-0.5 rounded bg-white/10">{meta?.version || 'v1.0'}</span>
              <StatusBadge status={meta?.status || 'Active'} dot={false} />
            </div>
            <div className="text-[11px] text-slate-300 mt-1">
              {rows.length} active controls · {groups.length} categories · last published by {meta?.lastUploadedBy || '—'} on {meta?.lastUploadedAt || '—'}
            </div>
          </div>
          <Code2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="fraud-live-kpis">
          <FraudKpi label="Total Controls" value={allRows.length} icon={ShieldCheck} tone="slate" testId="fraud-kpi-total" />
          <FraudKpi label="Categories" value={[...new Set(allRows.map((r) => r.fraudCategory))].length} icon={CheckCircle2} tone="emerald" testId="fraud-kpi-categories" />
          <FraudKpi label="Completion" value={`${completion}%`} icon={CheckCircle2} tone={completion >= 75 ? 'emerald' : completion >= 40 ? 'amber' : 'red'} testId="fraud-kpi-completion" />
          <FraudKpi label="Fraud Gaps" value={kpis.fraudGaps} icon={AlertTriangle} tone="red" testId="fraud-kpi-gaps" onClick={() => navigateFilter('no-or-na')} />
          <FraudKpi label="Missing Evidence" value={kpis.missingEvidence} icon={AlertTriangle} tone="violet" testId="fraud-kpi-missing-evidence" onClick={() => navigateFilter('missing-evidence')} />
          <FraudKpi label="Overdue Remediation" value={kpis.overdue} icon={AlertTriangle} tone="amber" testId="fraud-kpi-overdue" onClick={() => navigateFilter('overdue')} />
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mr-1">Filter:</span>
          {[
            { k: '', l: 'All' },
            { k: 'pending', l: 'Pending' },
            { k: 'no-or-na', l: 'Fraud gaps' },
            { k: 'missing-evidence', l: 'Missing evidence' },
            { k: 'overdue', l: 'Overdue' },
          ].map((f) => (
            <button
              key={f.k || 'all'}
              data-testid={`fraud-filter-${f.k || 'all'}`}
              onClick={() => navigateFilter(f.k)}
              className={cn(
                'px-2.5 py-1 rounded-md text-[10px] font-bold transition',
                (filter || '') === f.k ? 'bg-[#CC0000] text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100',
              )}
            >
              {f.l}
            </button>
          ))}
          <span className="ml-auto text-[11px] text-slate-500">Showing {rows.length} of {allRows.length}</span>
        </div>

        {filter && <FilterBanner activeFilter={filter} count={rows.length} totalCount={allRows.length} />}

        {filter && rows.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
            <p className="text-sm font-semibold text-slate-700">No fraud controls match this filter</p>
          </div>
        )}

        {groups.map(([categoryName, categoryRows]) => (
          <div key={categoryName} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100 bg-gradient-to-r from-sky-50/40 to-white flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-sky-100 flex items-center justify-center">
                <ShieldCheck className="w-4 h-4 text-sky-700" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900">{categoryName}</h3>
                <p className="text-[11px] text-slate-500">{categoryRows.length} {categoryRows.length === 1 ? 'control' : 'controls'} · evidence required for Yes responses</p>
              </div>
            </div>
            <div className="p-5 space-y-3">
              {categoryRows.map((row, i) => (
                <SchemaQuestionCard
                  key={row[schema.idField]}
                  schema={schema}
                  questionRow={row}
                  index={i}
                  questionTextKey="controlQuestion"
                  answerableKeys={ANSWERABLE}
                  accent="#0EA5E9"
                  readOnly={!canEdit}
                />
              ))}
            </div>
          </div>
        ))}
          </>
        )}
        </>)}
      </div>
    </div>
  );
}

function FraudKpi({ label, value, icon: Icon, tone = 'slate', testId, onClick }) {
  const toneMap = {
    slate:   'bg-slate-50 text-slate-700 border-slate-200',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    amber:   'bg-amber-50 text-amber-800 border-amber-200',
    red:     'bg-red-50 text-red-800 border-red-200',
    violet:  'bg-violet-50 text-violet-800 border-violet-200',
  };
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      onClick={onClick}
      data-testid={testId}
      className={cn('text-left rounded-xl border p-4 transition', toneMap[tone], onClick && 'hover:shadow-sm hover:-translate-y-px cursor-pointer')}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</span>
        {Icon && <Icon className="w-3.5 h-3.5 opacity-60" />}
      </div>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
    </Tag>
  );
}
